import DesktopWindowsSharp from "@mui/icons-material/DesktopWindowsSharp";
import LibraryBooksSharp from "@mui/icons-material/LibraryBooksSharp";
import LiveTvSharp from "@mui/icons-material/LiveTvSharp";
import PictureAsPdfSharp from "@mui/icons-material/PictureAsPdfSharp";
import {
  GridColumn as Column,
  GridCellProps,
} from "@progress/kendo-react-grid";
import {
  CollapsibleFormFiltersPageCard,
  DateCellContent,
  DateCellProps,
  DEFAULT_DATETIME_FORMAT,
  defaultGridProps,
  DropdownItemModel,
  GridProvider,
  InputType,
  isNullEmptyOrUndefined,
  MainGrid,
  MultiModalProvider,
  PageCard,
  PageCardRow,
  PageCardSection,
  PrimaryButton,
  Render,
  SecondaryButton,
  SmartInputBlock,
  TextIconButton,
  useGridState,
  useLocalState,
  useToasts,
} from "@techsharegov/react-ui";
import { AgendaEndpoints } from "Models/Routes";
import { Routes } from "Navigation";
import { useFileDownloader } from "Utilities";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Row } from "reactstrap";
import {
  MeetingsGridProps,
  UpcomingMeetingLocalState,
  useDetailedSearch,
} from "./CurrentAndUpcomingMeetings";
export const ArchivedMeetings: React.FC<MeetingsGridProps> = ({
  committeeId = undefined,
}) => {
  return (
    <GridProvider entityName="Meeting">
      <ArchivedMeetingsGrid committeeId={committeeId} />
    </GridProvider>
  );
};

export class MeetingSearch {
  constructor(committeeId?: string) {
    this.committeeId = committeeId;
  }
  description?: string;
  meetingType?: DropdownItemModel[];
  startDateFrom?: Date;
  startDateTo?: Date;
  committeeId?: string;
}

const ArchivedMeetingSearchFields: React.FC<MeetingsGridProps> = ({
  committeeId,
}) => {
  const [{ isCardOpen, toggleIsCardOpen }] =
    useLocalState<UpcomingMeetingLocalState>();

  const { updateCurrentSearch } = useDetailedSearch();

  return (
    <CollapsibleFormFiltersPageCard
      collapsibleTitle="Search Parameters"
      isOpen={isCardOpen}
      toggleIsOpen={toggleIsCardOpen}
      filterChipsProps={{
        onClear: updateCurrentSearch,
      }}
    >
      <PageCardRow>
        <PageCardSection thin short>
          <Row>
            <SmartInputBlock
              name="description"
              label="Meeting Title"
              type={InputType.Text}
            />

            <SmartInputBlock
              name="meetingTypes"
              label="Meeting Types"
              type={InputType.MultiSelect}
              readEndpoint={
                committeeId === undefined
                  ? AgendaEndpoints.meetingtypes.dropdownItems
                  : AgendaEndpoints.meetingtypes.dropdownItems +
                    "?committeeId=" +
                    committeeId
              }
            />
            <SmartInputBlock
              name="startDateFrom"
              label="Meeting Date From"
              type={InputType.DatePicker}
            />
            <SmartInputBlock
              name="startDateTo"
              label="Meeting Date To"
              type={InputType.DatePicker}
            />
          </Row>
          <DetailedSearchButton />
        </PageCardSection>
      </PageCardRow>
    </CollapsibleFormFiltersPageCard>
  );
};

export const ArchivedMeetingsGrid: React.FC<MeetingsGridProps> = ({
  committeeId,
}) => {
  const [{ searchValues, shouldClearGridData, setShouldClearGridData }] =
    useLocalState<UpcomingMeetingLocalState>();
  const { total, setData, setTotal } = useGridState();
  useEffect(() => {
    if (shouldClearGridData) {
      setData([]);
      setTotal(0);
      setShouldClearGridData(false);
    }
  }, [shouldClearGridData]);

  const UpdateAriaLabels = () => {
    setTimeout(() => {
      document.querySelectorAll("button.k-pager-nav").forEach(function (el) {
        var title = el.getAttribute("title");
        el.setAttribute("aria-label", title as string);
      });
      document
        .querySelectorAll("div.k-pager-numbers > button")
        .forEach(function (el) {
          var test = el.getAttribute("aria-label");
          var str = test?.replace("undefined", "page");
          el.setAttribute("aria-label", str as string);
        });
    }, 2500);
  };

  return (
    <PageCard title="Archived Meetings">
      <MultiModalProvider>
        <Render condition={committeeId === undefined}>
          <MainGrid
            readEndpoint={AgendaEndpoints.meetings.controllerActionString(
              "readArchived"
            )}
            search={searchValues}
            refreshOnSearchChange
            sortable={true}
            groupable={false}
            onFetchSuccess={UpdateAriaLabels}
            {...defaultGridProps}
            pageSize={50}
            pageable={{ buttonCount: 3 }}
            ariaLabel="Archived Meetings"
          >
            <Column field="description" title="Meeting Title" width="auto" />
            <Column field="meetingType" title="Meeting Type" width="auto" />
            <Column
              field="meetingStartDateTimeDisplay"
              title="Date/Time"
              width="160px"
            />
            <Column
              field="id"
              title="Documents"
              cell={ActionsCell}
              width="auto"
            />
            <Column
              field="video"
              title="Video"
              cell={PlayVideoCell}
              width="70"
            />
          </MainGrid>
        </Render>
        <Render condition={committeeId !== undefined}>
          <MainGrid
            readEndpoint={AgendaEndpoints.meetings.controllerActionString(
              "readArchived"
            )}
            search={{ ...searchValues, committeeId }}
            refreshOnSearchChange
            sortable={true}
            groupable={false}
            onFetchSuccess={UpdateAriaLabels}
            {...defaultGridProps}
            pageSize={50}
            pageable={{ buttonCount: 3 }}
            ariaLabel="Archived Meetings"
          >
            <Column field="description" title="Meeting Title" width="auto" />
            <Column
              field="meetingStartDateTime"
              title="Date/Time"
              width="160px"
              cell={DateCell}
            />
            <Column
              field="id"
              title="Documents"
              cell={ActionsCell}
              width="auto"
            />
            <Column
              field="video"
              title="Video"
              cell={PlayVideoCell}
              width="70"
            />
          </MainGrid>
        </Render>
      </MultiModalProvider>
    </PageCard>
  );
};

const ActionsCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  const { refreshGrid } = useGridState();
  const toast = useToasts();
  const { downloadFile } = useFileDownloader();
  const dataItem = props.dataItem;
  const downloadAgenda = async () => {
    try {
      var url =
        AgendaEndpoints.meetingattachments.controllerActionString("download") +
        "?id=" +
        dataItem.agendaAttachmentId;

      downloadFile(url, "MeetingAgenda");
      refreshGrid();
    } catch {
      toast.error("Error downloading document.");
    }
  };

  const downloadMinutes = async () => {
    try {
      var url =
        AgendaEndpoints.meetingattachments.controllerActionString("download") +
        "?id=" +
        dataItem.minutesAttachmentId;

      downloadFile(url, "MeetingMinutes");
      refreshGrid();
    } catch {
      toast.error("Error downloading document.");
    }
  };

  return (
    <td
      aria-colindex={props.ariaColumnIndex}
      colSpan={props.colSpan}
      className={props.className}
      role="gridcell"
    >
      <Render condition={dataItem?.isPublished}>
        <Link
          aria-label={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "html agenda"
          }
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "html agenda"
          }
          to={`${Routes.HtmlAgenda}/${dataItem?.id}`}
          target="blank"
        >
          <TextIconButton
            aria-hidden
            icon={DesktopWindowsSharp}
            className="single-line-btn"
          >
            Html Agenda
          </TextIconButton>
        </Link>
        <br />
      </Render>
      <Render condition={dataItem?.hasAgenda && dataItem?.isPublished}>
        <Link
          aria-label={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download agenda"
          }
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download agenda"
          }
          to="#"
          onClick={downloadAgenda}
        >
          <TextIconButton
            name={
              dataItem?.description +
              " " +
              dataItem?.meetingStartDateTimeDisplay +
              " " +
              "download agenda"
            }
            icon={PictureAsPdfSharp}
            className="single-line-btn"
          >
            Agenda
          </TextIconButton>
        </Link>
        <br />
      </Render>
      <Render condition={dataItem?.hasMinutes}>
        <Link
          aria-label={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download minutes"
          }
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "download minutes"
          }
          to="#"
          onClick={downloadMinutes}
        >
          <TextIconButton
            name={
              dataItem?.description +
              " " +
              dataItem?.meetingStartDateTimeDisplay +
              " " +
              "download minutes"
            }
            icon={LibraryBooksSharp}
            className="single-line-btn"
            tabIndex={0}
          >
            Minutes
          </TextIconButton>
        </Link>
      </Render>
    </td>
  );
};

const PlayVideoCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  const dataItem = props.dataItem;

  return (
    <td
      aria-colindex={props.ariaColumnIndex}
      colSpan={props.colSpan}
      className={props.className}
      role="gridcell"
    >
      {dataItem?.videoId != null && dataItem?.videoId !== "" && (
        <Link
          title={
            dataItem?.description +
            " " +
            dataItem?.meetingStartDateTimeDisplay +
            " " +
            "video"
          }
          to={{
            pathname: "https://www.youtube.com/watch?v=" + dataItem.videoId,
          }}
          target="blank"
        >
          <TextIconButton
            icon={LiveTvSharp}
            className="justify-center"
          ></TextIconButton>
        </Link>
      )}
    </td>
  );
};

const DateCell: React.FC<DateCellProps> = (props) => {
  return (
    <td
      aria-colindex={props.ariaColumnIndex}
      colSpan={props.colSpan}
      className={props.className}
      role="gridcell"
    >
      <DateCellContent dateFormat={DEFAULT_DATETIME_FORMAT} {...props} />
    </td>
  );
};

const DetailedSearchButton: React.FC = () => {
  const { formValues, runLastSearch, search } = useDetailedSearch();

  return (
    <div className="full-width flex-vertical-center">
      <div className="flex-grow" />
      <SecondaryButton className="mr-regular" onClick={runLastSearch}>
        Run Last Search
      </SecondaryButton>
      <PrimaryButton
        onClick={search}
        disabled={isNullEmptyOrUndefined(formValues)}
      >
        Search
      </PrimaryButton>
    </div>
  );
};
